import React, { useMemo } from "react";

/**
 * Lightweight SVG sparkline for daily error counts.
 * Props:
 *   series: [{ date, error, warning, info, total }]
 *   height: px (default 72)
 *   accent: color for the peak marker + last-point dot
 */
export default function ErrorsSparkline({ series = [], height = 72, accent = "#c45f4a" }) {
  const width = 100 * (series.length || 1);
  const stats = useMemo(() => {
    const values = series.map((d) => d.total || 0);
    const max = Math.max(1, ...values);
    const sum = values.reduce((a, b) => a + b, 0);
    const peakIdx = values.reduce((best, v, i) => (v > values[best] ? i : best), 0);
    return { max, sum, values, peakIdx, avg: sum / (series.length || 1) };
  }, [series]);

  if (!series.length) {
    return (
      <div className="h-[72px] flex items-center justify-center text-xs text-muted-foreground" data-testid="errors-sparkline-empty">
        No data
      </div>
    );
  }

  const step = width / series.length;
  const yFor = (v) => height - 8 - (v / stats.max) * (height - 20);
  const points = stats.values.map((v, i) => [i * step + step / 2, yFor(v)]);

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${height} L${points[0][0].toFixed(1)},${height} Z`;

  const last = series[series.length - 1];
  const peak = series[stats.peakIdx];
  const [peakX, peakY] = points[stats.peakIdx];
  const [lastX, lastY] = points[points.length - 1];

  return (
    <div className="relative w-full" data-testid="errors-sparkline">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
        <defs>
          <linearGradient id="errFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.28" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#errFill)" />
        <path d={linePath} fill="none" stroke={accent} strokeWidth="1.5" />
        <circle cx={peakX} cy={peakY} r="2.6" fill={accent} />
        <circle cx={lastX} cy={lastY} r="3.2" fill="#0b3d3a" />
      </svg>
      <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
        <span>{series[0]?.date}</span>
        <span>Peak {peak?.total} on {peak?.date}</span>
        <span>Last {last?.date} · {last?.total}</span>
      </div>
    </div>
  );
}
